import React, { useState } from "react";
import { Spin } from "antd";
import { MdClose } from "react-icons/md";
import deleteIcon from "../../Assets/schedule/delete.svg";
import { useHistory, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Notification from "../../services/Notification";
import SavingModal from '../../components/SavingModal';
import PageDataLoader from 'components/pageDataLoader';

import "./scheduleAddQuiz.css";
import { connect } from "react-redux";
import {
  postBroadcastQuizCreate,
  postBroadcastQuizQuestion,
  addBroadcastQuiz,
  postSaveBroadcastQuizzes,
} from "actions/digitalLibrary";
import { drawerBooleanState } from "actions/schedule";

const ScheduleAddQuiz = ({
  postBroadcastQuizCreate,
  postBroadcastQuizQuestion,
  dispatchAddBroadcastQuiz,
  postSaveBroadcastQuizzes,
  drawerBooleanState
}) => {
  const history = useHistory();
  const location = useLocation();

  // broadcastId = scheduleId passed from ScheduleDetailsDrawer via route state
  const scheduleId = location.state?.scheduleId;

  const isEdit = location.state?.isEdit;
  const initialData = location.state?.initialData;

  const [quizTitle, setQuizTitle] = useState("");
  const [isTimeBound, setIsTimeBound] = useState(false);
  const [timeLimit, setTimeLimit] = useState("");
  const [isGraded, setIsGraded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '', onConfirm: () => { } });
  const [isClosing, setIsClosing] = useState(false);

  // Last-saved baseline for the two toggle-gated inputs. Switching a toggle off
  // discards whatever was typed under it since the last save and restores this
  // instead, so switching it back on never resurrects an abandoned draft value:
  // blank on a new quiz, the saved value when editing an existing one. Refs
  // rather than state - nothing renders off them, they only feed the handlers.
  const savedTimeLimitRef = React.useRef("");
  // questionId -> { positiveMarks, negativeMarks }. Questions added after load
  // have no entry, so their marks fall back to blank.
  const savedMarksRef = React.useRef({});

  React.useEffect(() => {
    if (initialData) {
      setQuizTitle(initialData.title || initialData.name || "");
      const duration = initialData.timeBound || initialData.timebound || initialData.duration || initialData.timeLimit || initialData.time_limit || initialData.time_duration || 0;
      const savedTimeLimit = duration ? String(duration) : "";
      setTimeLimit(savedTimeLimit);
      savedTimeLimitRef.current = savedTimeLimit;
      setIsTimeBound(!!duration || initialData.isTimeBound === true || initialData.is_time_bound === true);
      const allQs = initialData.questions ||
        (Array.isArray(initialData.quizTitle) ? initialData.quizTitle[0]?.questions : null) || [];
      setIsGraded(allQs.some(q => q.positiveMarks > 0) || false);

      // Questions may be nested differently depending on API response shape
      const rawQuestions = initialData.questions ||
        (Array.isArray(initialData.quizTitle) ? initialData.quizTitle[0]?.questions : null) ||
        (Array.isArray(initialData.quiz_title) ? initialData.quiz_title[0]?.questions : null) ||
        [];

      if (rawQuestions && Array.isArray(rawQuestions) && rawQuestions.length > 0) {
        // Sort questions by ID to maintain original order during edit
        const sortedQuestions = [...rawQuestions].sort((a, b) => {
          const idA = parseInt(a.id);
          const idB = parseInt(b.id);
          if (!isNaN(idA) && !isNaN(idB)) return idA - idB;
          return 0;
        });

        const mappedQuestions = sortedQuestions.map((q) => {
          // Backend returns type:"" so we must INFER the type from the data
          const rawTypeStr = String(q.type || q.questionType || q.question_type || "").trim().toLowerCase();
          const hasOptions = Array.isArray(q.questionContent?.options) && q.questionContent.options.length > 0;
          const answerVal = q.answerContent?.answer;
          const isTF = answerVal === true || answerVal === false || answerVal === "True" || answerVal === "False";
          const isNumerical = !isTF && Array.isArray(answerVal) && answerVal.length > 0 && !hasOptions;

          let type = "MCQ"; // Default
          if (rawTypeStr.includes("t/f") || rawTypeStr.includes("true or false")) {
            type = "True or false";
          } else if (rawTypeStr.includes("numeric") || rawTypeStr.includes("numerical")) {
            type = "Numerical";
          } else if (rawTypeStr === "mcq") {
            type = "MCQ";
          } else {
            // Infer from data when type field is empty
            if (isTF) type = "True or false";
            else if (isNumerical) type = "Numerical";
            else type = "MCQ";
          }

          const apiOptions = q.questionContent?.options || q.options || [];
          const options = apiOptions.map(opt => ({
            text: opt.value || opt.text || "",
            isCorrect: opt.correct ?? opt.isCorrect ?? false
          }));

          // Robust answer retrieval
          let apiAnswer = q.answerContent?.answer;
          if (apiAnswer === undefined || apiAnswer === null) apiAnswer = q.answerContent?.answers?.[0];
          if (apiAnswer === undefined || apiAnswer === null) {
            if (Array.isArray(q.answerContent)) {
              apiAnswer = q.answerContent[0]?.answer ?? q.answerContent[0];
            } else {
              apiAnswer = q.answerContent;
            }
          }

          const correctAnswerStr = Array.isArray(apiAnswer) ? String(apiAnswer[0] || "") : String(apiAnswer || "");

          return {
            id: q.id || uuidv4(),
            type: type,
            text: q.questionContent?.statement || q.text || "",
            options: options.length > 0 ? options : [
              { text: "", isCorrect: false },
              { text: "", isCorrect: false },
            ],
            tfCorrect: type === "True or false" ? (apiAnswer === true || apiAnswer === "True" ? "True" : "False") : null,
            correctAnswer: type === "Numerical" ? correctAnswerStr : "",
            positiveMarks: String(q.positiveMarks || ""),
            negativeMarks: String(q.negativeMarks || ""),
          };
        });
        if (mappedQuestions.length > 0) {
          setQuestions(mappedQuestions);
          savedMarksRef.current = mappedQuestions.reduce((acc, q) => {
            acc[q.id] = { positiveMarks: q.positiveMarks, negativeMarks: q.negativeMarks };
            return acc;
          }, {});
        }
      }
    }
  }, [initialData]);

  const [questions, setQuestions] = useState([
    {
      id: uuidv4(),
      type: "MCQ",
      text: "",
      options: [
        { text: "", isCorrect: false },
        { text: "", isCorrect: false },
      ],
      tfCorrect: null,
      correctAnswer: "",
      positiveMarks: "",
      negativeMarks: "",
    },
  ]);

  const isQuestion1Filled = () => {
    if (questions.length === 0) return false;
    const q1 = questions[0];
    if (!q1.text.trim()) return false;
    if (q1.type === "MCQ") {
      const filledOptions = q1.options.filter((opt) => opt.text.trim().length > 0);
      return filledOptions.length >= 2;
    }
    return true;
  };

  const validate = () => {
    if (!quizTitle.trim()) {
      Notification.error("Validation Error", "Please enter a quiz title.");
      return false;
    }
    if (!scheduleId) {
      Notification.error("Error", "Schedule ID is missing. Please go back and try again.");
      return false;
    }
    if (isTimeBound && !timeLimit) {
      Notification.error("Validation Error", "Please enter a time limit.");
      return false;
    }
    if (questions.length === 0) {
      Notification.error("Validation Error", "Please add at least one question.");
      return false;
    }
    // Prevent duplicate titles for new quizzes
    if (!isEdit) {
      const existingQuizzes = location.state?.existingQuizzes || [];
      const isDuplicate = existingQuizzes.some(eq => eq.name.trim().toLowerCase() === quizTitle.trim().toLowerCase());
      if (isDuplicate) {
        Notification.error("Validation Error", "A quiz with this title already exists. Please choose a different title.");
        return false;
      }
    }

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const qNum = i + 1;
      if (!q.text.trim()) {
        Notification.error("Validation Error", `Please enter text for Question ${qNum}.`);
        return false;
      }
      if (q.type === "MCQ") {
        if (q.options.some((opt) => !opt.text.trim())) {
          Notification.error("Validation Error", `Please fill all options for Question ${qNum}.`);
          return false;
        }
        if (!q.options.some((opt) => opt.isCorrect)) {
          Notification.error("Validation Error", `Please select at least one correct answer for Question ${qNum}.`);
          return false;
        }
      } else if (q.type === "True or false") {
        if (!q.tfCorrect) {
          Notification.error("Validation Error", `Please select a correct answer for Question ${qNum}.`);
          return false;
        }
      } else if (q.type === "Numerical") {
        if (!q.correctAnswer.trim()) {
          Notification.error("Validation Error", `Please enter the correct answer for Question ${qNum}.`);
          return false;
        }
      }

      if (isGraded) {
        if (!q.positiveMarks || isNaN(parseFloat(q.positiveMarks)) || parseFloat(q.positiveMarks) <= 0) {
          Notification.error("Validation Error", `Please enter positive marks for Question ${qNum}.`);
          return false;
        }
      }
    }
    return true;
  };
  const handleSave = () => {
    if (!validate()) return;
    setModalConfig({
      title: 'Confirm',
      message: 'Are you sure want to save this quiz?',
      onConfirm: () => {
        setModalVisible(false);
        executeSave();
      }
    });
    setModalVisible(true);
  };

  const executeSave = () => {
    setIsSaving(true);

    const existingQuizzes = location.state?.existingQuizzes || [];

    // 1. Map new questions from current state to the API structure
    const currentMappedQuestions = questions.map((q) => {
      let apiType = q.type;
      if (apiType === "True or false") apiType = "T/F";
      // "Numerical" is this screen's label only - the backend rejects it with
      // "Numerical is not a valid test-question type" (500). Every other question
      // module in the app sends "Numeric"; see marketTest/addQuestions.jsx.
      if (apiType === "Numerical") apiType = "Numeric";

      let questionContent = { statement: q.text };

      // Every other quiz payload in the app sends questionContent.options for every
      // type except T/F, where it is deleted outright - see postAddQuiz in
      // actions/courseManagement.js and postBroadcastQuizQuestion in
      // actions/digitalLibrary.js. Leaving it off for Numeric made the server throw
      // "Cannot convert undefined or null to object". A Numeric question genuinely has
      // no options, so it sends an empty array rather than the blank placeholder rows
      // that still sit in local state from the MCQ default.
      if (apiType !== "T/F") {
        questionContent.options = apiType === "MCQ"
          ? (q.options || []).map((opt, idx) => ({
            option: String.fromCharCode(65 + idx), // 'A', 'B', 'C', 'D'
            value: opt.text,
            correct: opt.isCorrect,
          }))
          : [];
      }

      const result = {
        questionContent,
        // Switching Graded off restores the last-saved marks rather than clearing
        // them (see the toggle handler), so the ungraded payload still has to zero
        // them here.
        positiveMarks: isGraded ? Number(q.positiveMarks) || 0 : 0,
        negativeMarks: isGraded ? Number(q.negativeMarks) || 0 : 0,
        type: apiType,
        answerContent: {
          answer:
            apiType === "T/F"
              ? q.tfCorrect === "True"
              : apiType === "Numeric"
                ? [q.correctAnswer]
                : false,
        },
      };

      // Preserve the ID if it's an existing question from the backend.
      // Locally generated IDs (uuidv4) are 36 characters long.
      if (q.id && q.id.toString().length < 30) {
        result.id = q.id;
      }

      return result;
    });

    // 2. We only want to send the new/edited quiz, NOT all existing quizzes.
    const newQuizEntry = {
      name: quizTitle.trim() || "Untitled Quiz",
      questions: currentMappedQuestions,
      graded: isGraded,
      isPublished: initialData?.isPublished ?? initialData?.published ?? initialData?.hasPublishedForBroadcast ?? (initialData?.status === 'Published') ?? false,
      published: initialData?.isPublished ?? initialData?.published ?? initialData?.hasPublishedForBroadcast ?? (initialData?.status === 'Published') ?? false,
      publish: initialData?.isPublished ?? initialData?.published ?? initialData?.hasPublishedForBroadcast ?? (initialData?.status === 'Published') ?? false,
      hasPublishedForBroadcast: initialData?.isPublished ?? initialData?.published ?? initialData?.hasPublishedForBroadcast ?? (initialData?.status === 'Published') ?? false,
      status: (initialData?.isPublished ?? initialData?.published ?? initialData?.hasPublishedForBroadcast ?? (initialData?.status === 'Published')) ? 'Published' : 'Unpublished',
      ...(isTimeBound ? {
        duration: Number(timeLimit) || 0,
        timeBound: Number(timeLimit) || 0
      } : {}),
      ...(isEdit && (initialData?.id || initialData?.quizId) ? { id: initialData.id || initialData.quizId } : {})
    };

    // 3. Construct the final payload to ONLY update/create this specific quiz
    const payload = {
      occurrenceId: String(scheduleId),
      quizTitle: [newQuizEntry]
    };

    postSaveBroadcastQuizzes(
      payload,
      () => { setIsSaving(false); },
      (response) => {
        // Notification.success("Success", "Quiz saved successfully!");

        // isSaving is deliberately NOT cleared here - the blocking overlay has to hold
        // until the route actually changes, or the saved form goes live again first.
        // The navigation also used to sit inside a setTimeout(..., 500), which left the
        // finished form on screen and clickable for half a second after the save landed.
        // fromQuizSave: true triggers the re-fetch, and its loader, in the drawer.
        drawerBooleanState('quizzes');
        history.push("/schedule", {
          scheduleId,
          activeTab: "quizzes",
          fromQuizSave: true,
          currentDate: location.state?.currentDate
        });
      }
    );
  };

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: uuidv4(),
        type: "MCQ",
        text: "",
        options: [
          { text: "", isCorrect: false },
          { text: "", isCorrect: false },
        ],
        tfCorrect: null,
        correctAnswer: "",
        positiveMarks: "",
        negativeMarks: "",
      },
    ]);
  };

  const handleRemoveQuestion = (id) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((q) => q.id !== id));
    }
  };

  const updateQuestion = (id, field, value) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === id) {
          if (field === 'type' && q.type !== value) {
            const oldType = q.type;
            const cache = q.cache ? { ...q.cache } : {};
            
            // Save current state to cache
            cache[oldType] = {
              text: q.text,
              options: q.options,
              tfCorrect: q.tfCorrect,
              correctAnswer: q.correctAnswer,
            };
            
            // Restore from cache or use defaults
            const nextState = cache[value] || {
              text: "",
              options: [
                { text: "", isCorrect: false },
                { text: "", isCorrect: false },
              ],
              tfCorrect: null,
              correctAnswer: "",
            };
            
            return {
              ...q,
              type: value,
              cache,
              ...nextState
            };
          }
          return { ...q, [field]: value };
        }
        return q;
      })
    );
  };

  const updateOptionText = (qId, optIndex, value) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === qId) {
          const newOptions = q.options.map((opt, i) =>
            i === optIndex ? { ...opt, text: value } : opt
          );
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };

  const toggleCorrectAnswer = (qId, optIndex) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === qId) {
          const newOptions = q.options.map((opt, i) => ({
            ...opt,
            isCorrect: i === optIndex ? !opt.isCorrect : opt.isCorrect,
          }));
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };

  const addOption = (qId) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === qId) {
          if (q.options.length >= 4) return q;
          return {
            ...q,
            options: [...q.options, { text: "", isCorrect: false }],
          };
        }
        return q;
      })
    );
  };

  const removeOption = (qId, optIndex) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === qId) {
          if (q.options.length <= 2) return q;
          const newOptions = q.options.filter((_, i) => i !== optIndex);
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };


  const isFormDirty = (() => {
    // If creating new quiz, dirty if title or first question started
    if (!initialData) {
      return quizTitle.trim() !== "" ||
        isTimeBound ||
        isGraded ||
        questions.some(q => q.text.trim() !== "" || q.positiveMarks !== "" || (q.type === "MCQ" && q.options.some(opt => opt.text.trim() !== "")));
    }

    // If editing, compare with initialData
    try {
      const sameTitle = quizTitle === (initialData.title || initialData.name || "");
      const duration = initialData.timeBound || initialData.timebound || initialData.duration || initialData.timeLimit || initialData.time_limit || initialData.time_duration || 0;
      const sameTimeLimit = timeLimit === (duration ? String(duration) : "");
      const sameTimeBound = isTimeBound === (!!duration || initialData.isTimeBound === true || initialData.is_time_bound === true);
      const initialGraded = initialData.questions?.some(q => q.positiveMarks > 0) || false;
      const sameGraded = isGraded === initialGraded;

      const initialQuestions = initialData.questions || [];
      if (questions.length !== initialQuestions.length) return true;

      const sameQuestions = questions.every((q, idx) => {
        const initQ = initialQuestions[idx];
        const apiType = initQ.type === "T/F" ? "True or false" : initQ.type;
        if (q.type !== apiType) return false;
        if (q.text !== (initQ.questionContent?.statement || initQ.text || "")) return false;
        if (String(q.positiveMarks || '') !== String(initQ.positiveMarks || "")) return false;
        if (String(q.negativeMarks || '') !== String(initQ.negativeMarks || "")) return false;

        if (q.type === "MCQ") {
          const initOpts = initQ.questionContent?.options || initQ.options || [];
          if (q.options.length !== initOpts.length) return false;
          return q.options.every((opt, optIdx) => {
            const initOpt = initOpts[optIdx];
            return opt.text === (initOpt.value || initOpt.text || "") && opt.isCorrect === (initOpt.correct ?? initOpt.isCorrect ?? false);
          });
        } else if (q.type === "True or false") {
          const initAns = (initQ.answerContent?.answer === true || initQ.answerContent?.answer === "True") ? "True" : "False";
          return q.tfCorrect === initAns;
        } else if (q.type === "Numerical") {
          const initAns = String(initQ.answerContent?.answer || "");
          return q.correctAnswer === initAns;
        }
        return true;
      });

      return !(sameTitle && sameTimeLimit && sameTimeBound && sameGraded && sameQuestions);
    } catch (e) {
      return true;
    }
  })();

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      if (scheduleId) {
        console.log(scheduleId, "scheduleId")
        history.push("/schedule", {
          scheduleId,
          activeTab: "quizzes",
          currentDate: location.state?.currentDate
        });
        drawerBooleanState('quizzes')
      } else {
        history.goBack();
      }
    }, 10);
  };

  return (
    <div className="quiz-page-container">
      <div className="quiz-content-wrapper">

        {/* Header */}
        <div className="quiz-header">
          <div className="quiz-title">{isEdit ? "Edit a quiz" : "Add a quiz"}</div>
          <button className="quiz-close-button" onClick={handleClose}>
            <MdClose />
          </button>
        </div>

        {/* Section Title */}
        <div className="quiz-section-title">Quiz details</div>

        {/* Quiz Title Input */}
        <div className="quiz-form-group" style={{ position: 'relative', paddingTop: '25px' }}>
          <span
            className="quiz-floating-label"
            style={{
              position: 'absolute',
              left: 0,
              top: (quizTitle || focusedField === "quizTitle") ? '0' : '25px',
              fontSize: (quizTitle || focusedField === "quizTitle") ? '16px' : '15px',
              color: (quizTitle || focusedField === "quizTitle") ? '#636363' : '#9E9E9E',
              pointerEvents: 'none',
              transition: 'all 0.2s',
              zIndex: 1
            }}
          >
            Quiz title <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
          </span>
          <input
            className="quiz-styled-input"
            type="text"
            placeholder=""
            value={quizTitle}
            onChange={(e) => setQuizTitle(e.target.value)}
            onFocus={() => setFocusedField("quizTitle")}
            onBlur={() => setFocusedField(null)}
          />
        </div>

        {/* Time Bound Toggle Row */}
        <div className="quiz-toggle-row">
          <span className="quiz-toggle-label">Time bound</span>
          <label className="quiz-toggle-switch">
            <input
              className="quiz-toggle-input"
              type="checkbox"
              checked={isTimeBound}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsTimeBound(checked);
                // Switching off drops an unsaved time limit, so switching back on
                // starts from the saved value (blank on a new quiz), not the draft.
                if (!checked) setTimeLimit(savedTimeLimitRef.current);
              }}
            />
            <span className="quiz-toggle-slider"></span>
          </label>
          {isTimeBound && (
            <div className="quiz-time-limit-field" style={{ position: 'relative', paddingTop: '22px' }}>
              <span
                className="quiz-floating-label"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: (timeLimit || focusedField === "timeLimit") ? '0' : '22px',
                  fontSize: (timeLimit || focusedField === "timeLimit") ? '12px' : '14px',
                  color: (timeLimit || focusedField === "timeLimit") ? '#9E9E9E' : '#9E9E9E',
                  pointerEvents: 'none',
                  transition: 'all 0.2s',
                  zIndex: 1
                }}
              >
                Time limit (minutes) <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
              </span>
              <input
                className="quiz-styled-input"
                type="text"
                placeholder={(timeLimit || focusedField === "timeLimit") ? "e.g. 60" : ""}
                value={timeLimit}
                onFocus={() => setFocusedField("timeLimit")}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length > 3) return;
                  if (val === '' || /^\d+$/.test(val)) {
                    setTimeLimit(val);
                  }
                }}
              />
            </div>
          )}
        </div>

        <div className="quiz-toggle-row">
          <span className="quiz-toggle-label">Graded</span>
          <label className="quiz-toggle-switch">
            <input
              className="quiz-toggle-input"
              type="checkbox"
              checked={isGraded}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsGraded(checked);
                // Same rule as Time bound: unsaved marks are dropped on switch-off,
                // so switching back on shows the saved marks - and blank for any
                // question added since, which has nothing saved to restore.
                if (!checked) {
                  setQuestions((prev) =>
                    prev.map((q) => {
                      const saved = savedMarksRef.current[q.id];
                      return {
                        ...q,
                        positiveMarks: saved?.positiveMarks || "",
                        negativeMarks: saved?.negativeMarks || "",
                      };
                    })
                  );
                }
              }}
            />
            <span className="quiz-toggle-slider"></span>
          </label>
        </div>

        {/* Question Cards */}
        {questions.map((q, index) => (
          <div className="quiz-question-card" key={q.id}>
            {/* Card Header */}
            <div className="quiz-question-header">
              <h3 className="quiz-question-title">Question {index + 1}</h3>
              {questions.length > 1 && (
                <button
                  className="quiz-delete-question-button"
                  onClick={() => handleRemoveQuestion(q.id)}
                >
                  DELETE QUESTION
                </button>
              )}
            </div>

            {/* Question Type chips + Marks fields */}
            <div className="quiz-type-marks-row">
              <div className="quiz-type-section">
                <span className="quiz-label-visible">Question type</span>
                <div className="quiz-type-selector">
                  {["MCQ", "True or false", "Numerical"].map((type) => (
                    <button
                      key={type}
                      className={`quiz-type-chip ${q.type?.toLowerCase() === type.toLowerCase() ? "active" : ""}`}
                      onClick={() => updateQuestion(q.id, "type", type)}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {isGraded && (
                <div className="quiz-marks-fields">
                  <div className="quiz-marks-field">
                    <span className="quiz-positive-label">Positive marks <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span></span>
                    <input
                      className="quiz-marks-input"
                      type="text"
                      placeholder="e.g. 2 *"
                      value={q.positiveMarks}
                      onChange={(e) => {
                        const val = e.target.value;
                        const intPart = val.split('.')[0];
                        if (intPart.length > 2) return;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          updateQuestion(q.id, "positiveMarks", val);
                        }
                      }}
                    />
                  </div>
                  <div className="quiz-marks-field">
                    <span className="quiz-negative-label">Negative marks</span>
                    <input
                      className="quiz-marks-input"
                      type="text"
                      placeholder="e.g. -2"
                      value={q.negativeMarks}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (val === "" || val === "-") {
                          // Store "" for a lone "-", not the "-" itself. Backspacing the
                          // digit out of "-2" leaves "-" behind, and keeping it stranded
                          // a bare minus in a field the user had just emptied. Nothing is
                          // lost by dropping it: the sign is applied automatically below
                          // (a positive number is rewritten as negative), so the user
                          // never has to type it, and typing "-" then a digit still ends
                          // up negative. Number("-") was NaN -> 0 on save anyway.
                          updateQuestion(q.id, "negativeMarks", "");
                          return;
                        }

                        const numericVal = parseFloat(val);
                        if (!isNaN(numericVal)) {
                          if (numericVal === 0) {
                            val = val.replace("-", "");
                          } else if (numericVal > 0 && !val.startsWith("-")) {
                            val = "-" + val;
                          }
                        }

                        const absVal = val.startsWith("-") ? val.substring(1) : val;
                        const intPart = absVal.split(".")[0];
                        if (intPart.length > 2) return;
                        if (/^-?\d*\.?\d*$/.test(val)) {
                          updateQuestion(q.id, "negativeMarks", val);
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Question Text */}
            <div className="quiz-question-input-wrapper" style={{ position: 'relative', paddingTop: '25px' }}>
              <span
                className="quiz-floating-label"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: (q.text || focusedField === `q_${q.id}_text`) ? '0' : '25px',
                  fontSize: (q.text || focusedField === `q_${q.id}_text`) ? '16px' : '15px',
                  color: (q.text || focusedField === `q_${q.id}_text`) ? '#636363' : '#9E9E9E',
                  pointerEvents: 'none',
                  transition: 'all 0.2s',
                  zIndex: 1
                }}
              >
                Question
              </span>
              <input
                className="quiz-question-input"
                placeholder=""
                value={q.text}
                onChange={(e) => updateQuestion(q.id, "text", e.target.value)}
                onFocus={() => setFocusedField(`q_${q.id}_text`)}
                onBlur={() => setFocusedField(null)}
              />
            </div>

            {/* ── MCQ Options (vertical list) ── */}
            {q.type === "MCQ" && (
              <div>
                {q.options.map((opt, i) => (
                  <div
                    className={`quiz-option-row${focusedField === `q_${q.id}_opt_${i}` ? " is-focused" : ""}`}
                    key={i}
                  >
                    <div style={{ flex: 1, position: 'relative', paddingTop: '25px' }}>
                      <span
                        className="quiz-floating-label"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: (opt.text || focusedField === `q_${q.id}_opt_${i}`) ? '0' : '25px',
                          fontSize: (opt.text || focusedField === `q_${q.id}_opt_${i}`) ? '16px' : '15px',
                          color: (opt.text || focusedField === `q_${q.id}_opt_${i}`) ? '#636363' : '#9E9E9E',
                          pointerEvents: 'none',
                          transition: 'all 0.2s',
                          zIndex: 1
                        }}
                      >
                        Enter option {i + 1}
                      </span>
                      {/* The input and its in-field clear button share a relative box so the
                          button can be pinned to the input's right edge. */}
                      <div className="quiz-option-input-wrap">
                        <input
                          className="quiz-styled-input quiz-no-border quiz-option-input"
                          placeholder=""
                          value={opt.text}
                          onChange={(e) =>
                            updateOptionText(q.id, i, e.target.value)
                          }
                          onFocus={() => setFocusedField(`q_${q.id}_opt_${i}`)}
                          onBlur={() => setFocusedField(null)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              e.preventDefault();
                              updateOptionText(q.id, i, "");
                            }
                          }}
                        />
                        {/* Clears the typed option text - it does not delete the option.
                            Shown only while this input is focused and has something to clear.
                            onMouseDown + preventDefault keeps the input from blurring (and this
                            button from unmounting) before the click can land. */}
                        {focusedField === `q_${q.id}_opt_${i}` && opt.text ? (
                          <button
                            type="button"
                            className="quiz-option-clear"
                            title="Clear option"
                            aria-label={`Clear option ${i + 1}`}
                            tabIndex={-1}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              updateOptionText(q.id, i, "");
                            }}
                          >
                            <MdClose size={12} />
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <label className="quiz-correct-answer-group">
                      <input
                        type="checkbox"
                        className="quiz-correct-checkbox"
                        checked={opt.isCorrect}
                        onChange={() => toggleCorrectAnswer(q.id, i)}
                      />
                      <span className="quiz-correct-label">Correct answer</span>
                    </label>
                    {/* Always rendered so the row never reflows; disabled while the
                          question is down to its minimum of two options. */}
                    <button
                      type="button"
                      className="quiz-option-remove"
                      title={
                        q.options.length <= 2
                          ? "At least 2 options are required"
                          : "Remove option"
                      }
                      aria-label={`Remove option ${i + 1}`}
                      disabled={q.options.length <= 2}
                      onClick={() => removeOption(q.id, i)}
                    >
                      <img src={deleteIcon} alt="" width={20} height={20} />
                    </button>
                  </div>
                ))}
                {q.options.length < 4 && (
                  <button
                    className="quiz-add-option-button"
                    onClick={() => addOption(q.id)}
                  >
                    ADD AN OPTION
                  </button>
                )}
              </div>
            )}

            {/* ── True or False (two-column grid) ── */}
            {q.type === "True or false" && (
              <div className="quiz-tf-grid">
                {["True", "False"].map((val, i) => (
                  <div className="quiz-tf-option" key={val}>
                    <span className="quiz-floating-label">
                      Option {i + 1}
                    </span>
                    <div className="quiz-tf-top-row">
                      <span className="quiz-tf-value">{val}</span>
                      <div className="quiz-correct-answer-group">
                        <input
                          type="checkbox"
                          className="quiz-correct-checkbox"
                          checked={q.tfCorrect === val}
                          onChange={() =>
                            updateQuestion(
                              q.id,
                              "tfCorrect",
                              q.tfCorrect === val ? null : val
                            )
                          }
                        />
                        <span className="quiz-correct-label">Correct answer</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Numerical ── */}
            {q.type === "Numerical" && (
              <div className="quiz-numerical-wrapper">
                <span className="quiz-floating-label">Correct answer</span>
                <input
                  className="quiz-styled-input"
                  type="text"
                  placeholder="e.g. 12"
                  value={q.correctAnswer}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
                      updateQuestion(q.id, "correctAnswer", val);
                    }
                  }}
                />
              </div>
            )}
          </div>
        ))}


        {/* Bottom Row - Fixed Footer */}
        {isQuestion1Filled() && (
          <div className="quiz-fixed-footer">
            <button
              className="quiz-add-question-button"
              onClick={handleAddQuestion}
            >
              ADD QUESTION
            </button>
            <button
              className="quiz-save-button"
              onClick={handleSave}
              disabled={isSaving || (isEdit && !isFormDirty)}
              style={{
                opacity: (isSaving || (isEdit && !isFormDirty)) ? 0.7 : 1,
                cursor: (isSaving || (isEdit && !isFormDirty)) ? 'not-allowed' : 'pointer'
              }}
            >
              {isSaving ? "SAVING..." : "SAVE QUIZ"}
            </button>
          </div>
        )}

      </div>


      {/* Blocking overlay from the moment SAVE is confirmed until the route changes,
          or when closing the form. */}
      <PageDataLoader visible={isClosing || isSaving} />
      <SavingModal
        visible={modalVisible}
        title={modalConfig.title}
        message={modalConfig.message}
        onConfirm={modalConfig.onConfirm}
        onCancel={() => setModalVisible(false)}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  postBroadcastQuizCreate: (name, broadcastId, callBack, successCallback) =>
    dispatch(postBroadcastQuizCreate(name, broadcastId, callBack, successCallback)),
  postBroadcastQuizQuestion: (question, quizId, broadcastId, callBack, successCallback) =>
    dispatch(postBroadcastQuizQuestion(question, quizId, broadcastId, callBack, successCallback)),
  dispatchAddBroadcastQuiz: (quiz) => dispatch(addBroadcastQuiz(quiz)),
  postSaveBroadcastQuizzes: (quizzes, cb, scb) => dispatch(postSaveBroadcastQuizzes(quizzes, cb, scb)),
  drawerBooleanState: (value) => dispatch(drawerBooleanState(value)),
});

export default connect(null, mapDispatchToProps)(ScheduleAddQuiz);